import { useCallback, useMemo, useRef } from 'react'

import {
  Header,
  Page,
  Side,
  SideProps,
  Token,
  useMatchMedia,
} from '@revolut-internal/ui-kit'

import { ActionDropdown } from 'components/ActionDropdown/ActionDropdown'
import { useChannel } from 'hooks/useChannel'
import { useTicketResponseData } from 'hooks/useTicketResponseData'
import { useTicketsSorting } from 'hooks/useTicketsSorting'
import {
  useAssignedTicketIdsMutation,
  useAssignedTicketsInfiniteQuery,
} from 'modules/api/analytics/queries'
import { AgentTicketsRequest } from 'types'
import { pluralize } from 'utils/pluralize'

import { ResourcesPanelCopyResourcesButton } from '../../components/ResourcesPanel/ResourcesPanelCopyResourcesButton'
import { ResourcesPanelList } from '../../components/ResourcesPanel/ResourcesPanelList'
import { ResourcesPanelSection } from '../../components/ResourcesPanel/ResourcesPanelSection'

type AgentTicketsSideProps = Required<Pick<SideProps, 'open' | 'onClose'>> & {
  ticketsRequestParams: AgentTicketsRequest
  subtitle?: string
}

export const AgentTicketsSide = ({
  ticketsRequestParams,
  subtitle,
  open,
  onClose,
}: AgentTicketsSideProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { entityName } = useChannel()
  const isLargeScreen = useMatchMedia(Token.media.xxl)
  const { activeSorting, sortingOptions, sortingValue, onSortingSelect } =
    useTicketsSorting()

  const ticketsParams = useMemo(
    () => ({
      ...ticketsRequestParams,
      sortBy: activeSorting.sortBy,
      page: { sortOrder: activeSorting.sortOrder },
    }),
    [activeSorting.sortBy, activeSorting.sortOrder, ticketsRequestParams],
  )

  const { data: ticketsResponse = [], isPending } = useAssignedTicketsInfiniteQuery(
    ticketsParams,
    open,
  )
  const { mutateAsync: getTicketIds } = useAssignedTicketIdsMutation()
  const { tickets, ticketCount } = useTicketResponseData(ticketsResponse)

  const keyExtractor = useCallback((index: number) => tickets[index].id, [tickets])

  return (
    <Page.Side behaviour="cover" usePortal>
      <Side
        variant={isLargeScreen ? 'wide' : 'medium'}
        open={open}
        scrollRef={scrollRef}
        onClose={onClose}
      >
        <Header>
          <Header.CloseButton aria-label="Close" />
          <Header.Title>{subtitle}</Header.Title>
        </Header>

        <ResourcesPanelSection
          title={pluralize('ticket', ticketCount)}
          action={
            <ResourcesPanelCopyResourcesButton
              getResourceIds={
                ticketsParams ? () => getTicketIds(ticketsParams) : undefined
              }
              entityName={entityName}
            />
          }
          side={
            <ActionDropdown
              label="Agent tickets sorting"
              options={sortingOptions}
              value={sortingValue}
              onSelect={onSortingSelect}
            />
          }
          isPending={isPending}
          isEmpty={!ticketCount}
        >
          <ResourcesPanelList
            scrollRef={scrollRef}
            resources={tickets}
            keyExtractor={keyExtractor}
            entityName={entityName}
            isPending={isPending}
          />
        </ResourcesPanelSection>
      </Side>
    </Page.Side>
  )
}
